import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Contact = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'c8f90a9e-4628-4d3c-b21f-19864deec392',
          name: formData.name,
          email: formData.email,
          message: formData.message,
          subject: `New message from ${formData.name} - Laura Novak Website`
        })
      });

      const result = await response.json();

      if (result.success) {
        // Redirect to thank you page
        navigate('/thankyou');
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem sending your message. Please try again or email directly at laura.a.novak@gmail.com",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://lauranovakwrites.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Contact",
        "item": "https://lauranovakwrites.com/contact"
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact Laura Novak - Get In Touch"
        description="Contact author Laura Novak. Have a question about her books or want to connect? Reach out for inquiries, speaking engagements, or just to say hello."
        keywords="contact Laura Novak, author contact, book inquiries, speaking engagements, Laura Novak email"
        url="https://lauranovakwrites.com/contact"
        schema={schema}
      />
      
      {/* Hero Section */}
      <section className="relative py-32 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Mail className="w-16 h-16 text-amber-400 mx-auto mb-6" />
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
            Get In Touch
          </h1>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <p className="text-xl text-slate-600 text-center mb-12 leading-relaxed">
            Have a question about my books or just want to say hello? I'd love to hear from you.
          </p>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Honeypot field for spam protection - hidden from users */}
            <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
            
            <div>
              <Label htmlFor="name" className="text-slate-900 font-semibold mb-2 block text-lg">
                Name
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full h-14 border-slate-300 focus:border-amber-500 text-lg"
                placeholder="Your name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-900 font-semibold mb-2 block text-lg">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full h-14 border-slate-300 focus:border-amber-500 text-lg"
                placeholder="your.email@example.com"
              />
            </div>

            <div>
              <Label htmlFor="message" className="text-slate-900 font-semibold mb-2 block text-lg">
                Message
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={8}
                className="w-full border-slate-300 focus:border-amber-500 text-lg"
                placeholder="Your message ..."
              />
            </div>

            <Button 
              type="submit" 
              size="lg" 
              disabled={isSubmitting}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-xl font-semibold"
            >
              {isSubmitting ? 'Sending ...' : 'Send Message'}
            </Button>
          </form>
        </div>
      </section>

      {/* Decorative Separator before Footer */}
      <div className="h-2 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-60"></div>
    </div>
  );
};

export default Contact;